import React, { useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import axios from 'axios'
import { throttle } from 'lodash'
import Sprite from '~components/Sprite'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { setDoofinderLoaded } from '~redux/shop'

const SearchForm = ({ searchEnabled = true, isSuspended = false, doofinderLoaded = false, account = {}, setDoofinderLoaded = () => {}, className = '' }) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [showResults, setShowResults] = useState(false)
  const [useVendorDoofinder, setUseVendorDoofinder] = useState(false)

  const formRef = useRef(null)
  const searchInputRef = useRef(null)
  const resultsRef = useRef(null)
  const cancelToken = useRef(null)

  const search = useCallback(
    throttle(() => {
      if (cancelToken.current) {
        cancelToken.current.cancel('Cancelled by user')
        cancelToken.current = null
      }

      if (!query) {
        setResults([])
        setShowResults(false)
        return
      }

      cancelToken.current = axios.CancelToken.source()

      axios.get('https://eu1-search.doofinder.com/5/search', {
        cancelToken: cancelToken.current.token,
        params: {
          hashid: window.Panache.doofinder.hash,
          query
        }
      })
        .then(response => {
          setResults(response.data.results)
        })
    }, 200, { leading: false }),
    [query]
  )

  const handleClickOutside = (e) => {
    if (formRef.current && !formRef.current.contains(e.target)) {
      setShowResults(false)
    }
  }

  const handleDocumentKeyup = (e) => {
    if (e.code === 'Slash' && ['input', 'textarea'].indexOf(e.target.nodeName.toLowerCase()) === -1) {
      searchInputRef.current.focus()
    }

    if (!useVendorDoofinder) {
      search()
    }
  }

  useEffect(() => {
    setSearchProviderState()
    if (!useVendorDoofinder) return

    loadDoofinder()

    document.addEventListener('keyup', handleDocumentKeyup)
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keyup', handleClickOutside)

    return () => {
      document.removeEventListener('keyup', handleDocumentKeyup)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keyup', handleClickOutside)
    }
  }, [useVendorDoofinder])

  const setSearchProviderState = () => {
    const usesVendorDoofinder = !!window.Panache.doofinder.useVendorSearch
    setUseVendorDoofinder(usesVendorDoofinder)
  }

  const handleQueryChange = (e) => {
    const searchQuery = e.target.value ?? ''
    setQuery(searchQuery)
  }

  const loadDoofinder = () => {
    if (doofinderLoaded) return
    setDoofinderLoaded(true)

    const script = document.createElement('script')
    script.src = `${window.Panache.storeUrl}/js/doofinder.js?v=${window.Panache.doofinder.assetVersion}&country=${account.stockist.country}&hash=${window.Panache.doofinder.hash}`
    script.async = true
    script.id = 'doofinder_script'
    document.body.appendChild(script)
  }

  return (
    <div className={classNames('relative flex-1 z-40 h-12', className)} ref={formRef}>
      <label>
        <span className="sr-only">
          Search by style code or product name
        </span>

        <input
          ref={searchInputRef}
          value={query}
          onChange={(e) => handleQueryChange(e)}
          type="search"
          name="search"
          disabled={!searchEnabled || isSuspended}
          placeholder="Search by style code or product name"
          className={classNames(
            'relative block text-charcoal focus:bg-white focus:border-coral duration-200 ease-in-out h-full w-full transition-colors rounded-3xl p-4 pr-12 border border-pink color-charcoal placeholder-charcoal outline-none z-10 bg-white lg:bg-lightGrey xl:p-6 xl:pr-10',
            { 'opacity-40': !searchEnabled }
          )}
          autoComplete="off"
        />
      </label>

      <button className={classNames('w-10 h-full flex flex-col cursor-pointer absolute top-0 bottom-0 right-1 z-20 xl:right-3')} type="submit" value="submit" aria-label="Submit">
        <span className={classNames('block m-auto w-6 h-6')} aria-hidden="true">
          <Sprite name="search" />
        </span>
      </button>

      {showResults && results.length > 0 && (
        <div className={classNames('w-full bg-white text-charcoal shadow-3xl absolute pt-6 top-1/2 z-0 overflow-y-auto max-h-80 sm-landscape:max-h-75vh', className)} ref={resultsRef}>
          <div className={classNames('w-full py-6 px-4 xl:px-6')}>
            {results.map((result, index) => {
              const [styleCode, productName, colourName] = result.title.split(' ')
              const rangeName = result.categories === styleCode ? null : result.categories[0]

              if (!rangeName) {
                return null
              }

              const link = result.link.replace(/.*\/shop\//, '/shop/')

              return (
                <Link
                  to={`${link}`}
                  className={classNames(
                    'text-xs block focus:underline hover:underline',
                    { 'mb-8': results.length - 1 !== index }
                  )}
                  key={result.id}
                  onClick={() => setShowResults(false)}
                >
                  {[result.brand, styleCode, productName, colourName].join(' | ')}
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

SearchForm.propTypes = {
  searchEnabled: PropTypes.bool,
  className: PropTypes.string,
  doofinderLoaded: PropTypes.bool,
  setDoofinderLoaded: PropTypes.func,
  account: PropTypes.object,
  isSuspended: PropTypes.bool
}

const mapStateToProps = (state) => {
  return {
    doofinderLoaded: state.shop.doofinderLoaded,
    account: state.auth.account,
    searchEnabled: state.site.searchEnabled,
    isSuspended: state.auth.isSuspended
  }
}

const mapDispatchToProps = (dispatch) => ({
  setDoofinderLoaded: (value) => { dispatch(setDoofinderLoaded(value)) }
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchForm)
